import {
    ActivityRecordType,
    CalendarEventParentKind,
    HomeCareLeadId,
    PlacementId,
    ReferenceBusinessId,
    ReferenceContactId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";

export type CalendarEventDto = inferProcedureOutput<
    AppRouter["calendarEvents"]["create"]
>;

interface ActivityParentBase {
    name: string;
    address: string | null;
}

export type ReferralParent =
    | ({ kind: "contact"; id: ReferenceContactId } & ActivityParentBase)
    | ({ kind: "organization"; id: ReferenceBusinessId } & ActivityParentBase);

export type VisitParent =
    | ReferralParent
    | ({ kind: "homeCareLead"; id: HomeCareLeadId } & ActivityParentBase);

export type ActivityParent =
    | VisitParent
    | ({ kind: "client"; id: PlacementId } & ActivityParentBase);

export type ActivityParentKind = ActivityParent["kind"];

export const activityRecordTypeForActivityParentKind = (
    kind: ActivityParentKind,
): ActivityRecordType => {
    switch (kind) {
        case "contact":
            return "referenceContact";
        case "organization":
            return "referenceBusiness";
        case "client":
            return "placement";
        case "homeCareLead":
            return "homeCare";
    }
    const exhaustiveCheck: never = kind;
    throw new Error(`Unhandled activity parent kind ${String(exhaustiveCheck)}`);
};

export const visitParentFor = (parent: ActivityParent): VisitParent | null =>
    parent.kind === "client" ? null : parent;

export const parentRecordPath = (kind: CalendarEventParentKind, id: string): string => {
    switch (kind) {
        case "organization":
            return `/references/organizations/show/${id}`;
        case "contact":
            return `/references/contacts/show/${id}`;
        case "homeCareLead":
            return `/home-care-clients/show/${id}`;
    }
    const exhaustiveCheck: never = kind;
    throw new Error(`Unhandled parent kind ${String(exhaustiveCheck)}`);
};

interface VisitParentRef {
    parentKind: CalendarEventParentKind;
    parentId: string;
}

export const visitParentPath = (visit: VisitParentRef): string =>
    parentRecordPath(visit.parentKind, visit.parentId);

type VisitAddresses = Pick<
    CalendarEventDto,
    "clientAddress" | "businessAddress" | "communityAddress"
>;

export const visitAddress = (visit: VisitAddresses): string | null =>
    visit.clientAddress ?? visit.businessAddress ?? visit.communityAddress ?? null;
